import type { OperationFile } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { payloadClient } from '.';
import type { FindPayloadApiClientParams } from './types';

export const operationFileKeys = {
  all: ['operation-files'] as const,
  lists: () => [...operationFileKeys.all, 'list'] as const,
  list: (params: OperationFileQueryParams) => [...operationFileKeys.lists(), params] as const,
  details: () => [...operationFileKeys.all, 'detail'] as const,
  detail: (id: string) => [...operationFileKeys.details(), id] as const,
  blob: (id: string | null) => [...operationFileKeys.all, 'blob', id] as const,
} as const;

export type OperationFileQueryParams = FindPayloadApiClientParams<
  'operation-files',
  OperationFile
> & {
  disabled?: boolean;
};

export interface OperationFileUpdateParams {
  id: string;
  data: Partial<OperationFile>;
}

export const useOperationFiles = (params?: OperationFileQueryParams) => {
  return useQuery({
    queryKey: operationFileKeys.list(params ?? {}),
    enabled: !params?.disabled,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'operation-files',
        ...params,
      });
      return response;
    },
  });
};

export const useOperationFile = (id: string, params?: { disabled?: boolean }) => {
  return useQuery({
    queryKey: operationFileKeys.detail(id),
    enabled: !!id && !params?.disabled,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'operation-files',
        id,
      });
      return response;
    },
  });
};

export const useOperationFileBlobUrl = (operationFileId: string | null) => {
  return useQuery({
    queryKey: operationFileKeys.blob(operationFileId),
    enabled: !!operationFileId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!operationFileId) return null;

      const fileDoc = await payloadClient.findById({
        collection: 'operation-files',
        id: operationFileId,
      });

      if (!fileDoc?.url) return null;

      const request = await payloadClient.createRequest(fileDoc.url.replace('/api', ''), {
        method: 'GET',
      });
      const response = await payloadClient.fetcher(request);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
  });
};

export const useUploadOperationFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, data }: { file: File; data: Partial<OperationFile> }) => {
      const response = await payloadClient.create({
        collection: 'operation-files',
        data,
        file,
      });
      return response;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: operationFileKeys.all });
      const operationId =
        typeof variables.data.operation === 'string'
          ? variables.data.operation
          : (variables.data.operation as any)?.id;
      if (operationId) {
        queryClient.invalidateQueries({ queryKey: ['operations', 'detail', operationId] });
      }
    },
  });
};

export const useUpdateOperationFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: OperationFileUpdateParams) => {
      const response = await payloadClient.updateById({
        collection: 'operation-files',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: operationFileKeys.all });
      const operationId =
        typeof variables.data.operation === 'string'
          ? variables.data.operation
          : (variables.data.operation as any)?.id;
      if (operationId) {
        queryClient.invalidateQueries({ queryKey: ['operations', 'detail', operationId] });
      }
    },
  });
};
